import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useEffect, useState } from 'react';

import {
  type EventLogDataType,
  type EventLogInputType,
  type EventLogType,
  mutateNoteCreate,
  mutateNoteDelete,
  mutateNoteUpdate,
} from '@/api/eventLog';
import { Button } from '@/components/buttons';
import { Results } from '@/components/Results';
import { Link } from '@/components/typography';
import { useNotification } from '@/providers/Notification';

import NoteDeleteDialog from './NoteDeleteDialog';
import NotesPanelDrawerEdit from './NotesPanelDrawerEdit';
import NotesPanelDrawerTable from './NotesPanelDrawerTable';

type Props = {
  isOpen?: boolean;
  data?: EventLogType[];
  isLoading?: boolean;
  error: Error | null;
  selectedItem?: EventLogType;
  onSelectionChange?: (note?: EventLogType) => void;
  onNoteCreate?: (note: EventLogDataType) => void;
  onNoteUpdate?: (note: EventLogDataType) => void;
  onNoteDelete?: (noteId: string) => void;
};

const NotesPanelDrawer = ({
  data,
  isLoading,
  error,
  selectedItem,
  onSelectionChange,
  onNoteCreate,
  onNoteUpdate,
  onNoteDelete,
  isOpen,
}: Props) => {
  const { pushNotification, removeNotification } = useNotification();
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteToEdit, setNoteToEdit] = useState<EventLogType | undefined>(undefined);
  const [noteToDelete, setNoteToDelete] = useState<EventLogType | undefined>(undefined);
  const [isSavingNote, setSavingNote] = useState(false);

  const noNotes = data?.length === 0;

  const resetNoteForm = () => {
    setShowAddNote(false);
    setNoteToEdit(undefined);
  };

  useEffect(() => {
    if (!isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      resetNoteForm();
    }
  }, [isOpen]);

  const handleSaveNote = async (note: EventLogInputType) => {
    setSavingNote(true);
    removeNotification({ type: 'error' });

    try {
      if (note.id) {
        const newNote = await mutateNoteUpdate(note.id, note.rawDetails.toString(), note.date);
        onNoteCreate?.(newNote);
      } else {
        const updatedNote = await mutateNoteCreate(note.rawDetails.toString(), note.date);
        onNoteUpdate?.(updatedNote);
      }
    } catch {
      pushNotification({
        type: 'error',
        message: t`Failed to save note. Please try again or contact support if the issue persists.`,
      });
    }
    setSavingNote(false);
    resetNoteForm();
  };

  const handleEditNote = (note: EventLogType) => {
    setShowAddNote(false);
    setNoteToEdit(note);
  };

  const handleDeleteNote = async () => {
    try {
      if (noteToDelete) {
        await mutateNoteDelete(noteToDelete.id);
        onNoteDelete?.(noteToDelete.id);
      }
    } catch {
      pushNotification({
        type: 'error',
        message: t`Failed to delete note. Please try again or contact support if the issue persists.`,
      });
    }
    setNoteToDelete(undefined);
  };

  return (
    <div className="flex flex-col gap-y-2 px-3.5 pt-1 pb-5">
      {!!(showAddNote || noteToEdit) && (
        <NotesPanelDrawerEdit
          defaultNote={noteToEdit}
          isSaving={isSavingNote}
          onSave={handleSaveNote}
          onCancel={resetNoteForm}
        />
      )}
      {!(showAddNote || noteToEdit) && !!noNotes && (
        <Results
          className="pt-8"
          subtitle={
            <Trans>
              You have no notes during this time period. Add a note if anything interesting
              happened.
            </Trans>
          }
          extra={
            <Button variant="primary" onClick={() => setShowAddNote(true)}>
              <Trans>Add Note</Trans>
            </Button>
          }
        />
      )}
      {!(showAddNote || noteToEdit) && !noNotes && (
        <>
          <Link className="self-end" variant="caption1" onClick={() => setShowAddNote(true)}>
            <Trans>Add Note</Trans>
          </Link>
          <NotesPanelDrawerTable
            data={data}
            isLoading={isLoading}
            error={error}
            selectedItem={selectedItem}
            onSelectionChange={onSelectionChange}
            onNoteEdit={handleEditNote}
            onNoteDelete={setNoteToDelete}
          />
        </>
      )}
      <NoteDeleteDialog
        open={noteToDelete != null}
        onCancel={() => setNoteToDelete(undefined)}
        onDelete={handleDeleteNote}
      />
    </div>
  );
};

export default NotesPanelDrawer;
